// ABOUTME: Modal that displays singles breakdown for a product in echeck orders.
// ABOUTME: Shows selectable groups by productAmount with order count and future addon support.

import { Box, ButtonBase, Checkbox, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../theme";
import SHModal from "../../../../components/theme/Modal/SHModal";
import ButtonGroup from "../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../components/theme/buttons/Shbutton";
import Subheading from "../../../../components/theme/text/Subheading";
import Text from "../../../../components/theme/text/Text";
import { useAddonsFromOrdersQuery, useFinalizeOrdersMutation } from "../../../../newapi/global/echeckSlice";
import { addAction } from "../../../../newapi/global/actionProgressSlice";
import { showToastMessage, showToastMessageRequestError } from "../../../../utils/toasts";

const MSinglesOverview = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();

    const [finalizeMultiple, { isLoading: finalizeMultipleLoading }] = useFinalizeOrdersMutation();

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const [selectedIndices, setSelectedIndices] = useState([]);

    const singles = props.singles || [];

    const allOrderIds = useMemo(() =>
        singles.flatMap(s => s.orderIds),
        [singles]
    );

    const { data: addonsData, isLoading: addonsLoading, isError: addonsError } = useAddonsFromOrdersQuery(
        { orderIds: allOrderIds },
        { skip: !props.open || allOrderIds.length === 0 }
    );

    const getAddonsForSingle = (productAmount) => {
        if (addonsError || !addonsData?.addons) return [];
        return addonsData.addons.find(entry => entry.productAmount === productAmount)?.addons || [];
    };

    const toggleSelect = (index) => {
        setSelectedIndices(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const selectAll = () => {
        if (selectedIndices.length === singles.length) {
            setSelectedIndices([]);
        } else {
            setSelectedIndices(singles.map((_, i) => i));
        }
    };

    const handleClose = () => {
        setSelectedIndices([]);
        props.onClose();
    };

    const selectedOrderIds = useMemo(() =>
        selectedIndices.flatMap(i => singles[i].orderIds),
        [selectedIndices, singles]
    );

    const handleConfirm = () => {
        if (finalizeMultipleLoading || selectedOrderIds.length === 0) return;

        finalizeMultiple({
            orderIds: selectedOrderIds,
            pickingJobId: props.pickingJobId,
            workspaceId: props.workspaceId,
        })
        .unwrap()
        .then(() => {
            dispatch(addAction({
                type: "shipping_progress_individual_product_batch",
                entityType: "pickingjob",
                entityId: props.pickingJobId,
                entity: {
                    sku: props.productSku,
                    description: props.productDescription,
                }
            }));
            handleClose();
        })
        .catch(err => {
            showToastMessageRequestError(t("label_creation_failed"), t, err);
        });
    };

    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box sx={{
                textAlign: "center",
                paddingY: 4,
                paddingX: 5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}>
                <Subheading>
                    {props.productDescription || t("singles_overview")}
                </Subheading>
                {props.productSku &&
                    <Text light>{props.productSku}</Text>
                }
            </Box>

            <Box sx={{
                background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                padding: 3,
                pt:2,
                pb: 1,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight:500,
                overflow:"auto",
            }}>
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                }}>
                    <Text light>{singles.length === 1 ? `${singles.length} ${t("group")}` : `${singles.length} ${t("groups")}`}</Text>
                    <ButtonBase
                        onClick={selectAll}
                        sx={{
                            px: 1.5,
                            height: 24,
                            borderRadius: 3,
                            fontSize: 13,
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 500,
                            color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                        }}
                    >
                        {selectedIndices.length === singles.length ? t("deselect_all") : t("select_all")}
                    </ButtonBase>
                </Box>

                {singles.map((single, idx) => {
                    const isSelected = selectedIndices.includes(idx);
                    const addons = getAddonsForSingle(single.productAmount);

                    return (
                        <Box
                            key={idx}
                            onClick={() => toggleSelect(idx)}
                            sx={{
                                cursor: "pointer",
                                background: isSelected
                                    ? theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100]
                                    : (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0]),
                                border: isSelected
                                    ? `1px solid ${theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`
                                    : `1px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200]}`,
                                boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                borderRadius: 5,
                                display: "flex",
                                flexDirection: "column",
                                transition: "background 0.15s, border 0.15s",
                            }}
                        >
                            <Box sx={{
                                height: addons.length > 0 ? "unset" : 40,
                                pt:addons.length > 0 ? 1.5 : 0,
                                display: "flex",
                                alignItems: "center",
                                pl: 0.5,
                                pr: addons.length > 0 ? 1.5 : 0.5,
                            }}>
                                <Checkbox
                                    checked={isSelected}
                                    size="small"
                                    sx={{
                                        color: colors.txt["secondary"] ,
                                        '&.Mui-checked': {
                                            color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]
                                        },
                                    }}
                                />

                                <Box sx={{
                                    height: 32,
                                    lineHeight: "32px",
                                    px: 2,
                                    borderRadius: 4,
                                    background: isSelected ? theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100] : theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                }}>
                                    <Text><b>{single.orderIds.length}</b> {t("orders")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        ml: "auto",
                                        height: 32,
                                        lineHeight: "32px",
                                        px: 1.5,
                                        borderRadius: 4,
                                        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
                                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                        display:"flex",
                                        alignItems:"center",
                                        pr:0.5,
                                        gap:1,
                                    }}
                                >
                                    <Text light>{t("product_amount")}</Text>
                                    <Box
                                        sx={{
                                            height: 24,
                                            lineHeight: "24px",
                                            px: 1.5,
                                            borderRadius: 3,
                                            background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                        }}
                                    >
                                        <Text semibold>{single.productAmount}</Text>
                                    </Box>
                                </Box>
                            </Box>

                            {addons.length > 0 &&
                                <Box sx={{
                                    flexShrink:0,
                                    mx:1.5,
                                    px: 2,
                                    py: 1,
                                    pl:1.5,
                                    my:1.5,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.5,
                                    background:isSelected ? theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0] : theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
                                    borderRadius:3,
                                    border: `1px solid ${colors.grey[200]}`
                                }}>
                                    {addons.map((addon, addonIdx) => (
                                        <Box key={addonIdx} sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}>
                                            <Box
                                                sx={{
                                                    background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                    px:1.5,
                                                    height:24,
                                                    borderRadius:4,
                                                }}
                                            >
                                                <Text light fontSize={13}>+{addon.amount}x</Text>
                                            </Box>
                                            <Text fontSize={13}>{addon.description}</Text>
                                            <Text light fontSize={13}>({addon.sku})</Text>
                                        </Box>
                                    ))}
                                </Box>
                            }
                        </Box>
                    );
                })}
            </Box>

            <Box component="footer" sx={{
                display: "flex",
                width: 1,
                paddingBottom: isStandAlone ? "32px" : "16px",
                alignItems: "center",
                paddingRight: 3,
                zIndex: 1058,
                position: "relative",
                paddingTop: 2,
                background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                borderBottomLeftRadius: 24,
                borderBottomRightRadius: 24,
                justifyContent: "space-between",
                "@media screen and (max-width: 47.9375em)": { paddingRight: 0 }
            }}>
                <Shbutton
                    isCancel={true}
                    className={"always-visible"}
                    onClick={handleClose}
                    color="primary"
                    variant="text"
                    startIcon={<ArrowRightAltIcon />}
                >
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={selectedIndices.length === 0}
                        isLoading={finalizeMultipleLoading}
                    >
                        {t("print_label")} ({selectedOrderIds.length})
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

export default MSinglesOverview;
